import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import TicketBoard from "./pages/TicketBoard";
import TicketDetail from "./pages/TicketDetail";
import AdminDashboard from "./pages/AdminDashboard";
import KnowledgeBase from "./pages/KnowledgeBase";
import UserManagement from "./pages/UserManagement";
import AdminSettings from "./pages/AdminSettings";
import ExternalConnections from "./pages/ExternalConnections";
import PublicTicketForm from "./pages/PublicTicketForm";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ResendVerification from "./pages/ResendVerification";
import ViewCredentials from "./pages/ViewCredentials";
import AuditLogs from "./pages/AuditLogs";
import ChangeRequestDetail from "./pages/ChangeRequestDetail";
import HRRequests from "./pages/HRRequests";
import HRRequestDetail from "./pages/HRRequestDetail";
import { ThemeProvider } from "./contexts/ThemeContext";
import { RBACProvider, useRBAC } from "./contexts/RBACContext";
import { toast } from "sonner";
import InstallPWA from "./components/InstallPWA";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Inactivity timeout in milliseconds (2 hours)
const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

// Axios interceptor for auth token (fallback for environments where cookies are stripped by proxy)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewRole, setPreviewRole] = useState(null);
  const [previewDealership, setPreviewDealership] = useState(null);
  const [previewDepartment, setPreviewDepartment] = useState(null);
  const inactivityTimer = useRef(null);

  // Inactivity logout handler
  const handleInactivityLogout = useCallback(() => {
    if (user) {
      localStorage.removeItem("token");
      axios.post(`${API}/auth/logout`).catch(() => {});
      setUser(null);
      toast.warning("You have been logged out due to inactivity", {
        duration: 5000,
      });
      window.location.href = "/";
    }
  }, [user]);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    if (user) {
      inactivityTimer.current = setTimeout(handleInactivityLogout, INACTIVITY_TIMEOUT);
    }
  }, [user, handleInactivityLogout]);

  // Set up activity listeners when user is logged in
  useEffect(() => {
    if (user) {
      // Activity events to track
      const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
      
      // Add event listeners
      activityEvents.forEach(event => {
        document.addEventListener(event, resetInactivityTimer, { passive: true });
      });

      // Start the initial timer
      resetInactivityTimer();

      // Cleanup
      return () => {
        activityEvents.forEach(event => {
          document.removeEventListener(event, resetInactivityTimer);
        });
        if (inactivityTimer.current) {
          clearTimeout(inactivityTimer.current);
        }
      };
    }
  }, [user, resetInactivityTimer]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  };

  const logout = async () => {
    localStorage.removeItem("token");
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
    window.location.href = "/";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
    <RBACProvider user={user}>
      <div className="App">
        <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Login setUser={setUser} />
              )
            }
          />
          <Route
            path="/register"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Register setUser={setUser} />
              )
            }
          />
          <Route
            path="/submit-ticket"
            element={<PublicTicketForm />}
          />
          <Route
            path="/view-credentials/:accessToken"
            element={<ViewCredentials />}
          />
          <Route
            path="/verify-email"
            element={<VerifyEmail />}
          />
          <Route
            path="/forgot-password"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route
            path="/reset-password"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ResetPassword />
              )
            }
          />
          <Route
            path="/resend-verification"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ResendVerification />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                <Dashboard 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/tickets"
            element={
              user ? (
                <TicketBoard 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/tickets/:ticketId"
            element={
              user ? (
                <TicketDetail 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && (user.role === "admin" || user.role === "super_admin" || user.role === "site_admin") ? (
                <AdminDashboard 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/knowledge-base"
            element={
              user ? (
                <KnowledgeBase 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              user && (user.role === "admin" || user.role === "super_admin" || user.role === "site_admin") ? (
                <UserManagement 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user && (user.role === "admin" || user.role === "super_admin") ? (
                <AdminSettings 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/connections"
            element={
              user && (user.role === "admin" || user.role === "super_admin") ? (
                <ExternalConnections 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                  setPreviewDealership={setPreviewDealership}
                  previewDepartment={previewDepartment}
                  setPreviewDepartment={setPreviewDepartment}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/audit-logs"
            element={
              user && (user.role === "admin" || user.role === "super_admin") ? (
                <AuditLogs user={user} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/hr-requests"
            element={
              user && user.role !== "end_user" && (user.role === "site_admin" || user.role === "admin" || user.role === "super_admin" || (user.role === "department_admin" && ["IT", "HR", "Administration"].includes(user.department))) ? (
                <HRRequests 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/hr-requests/:requestId"
            element={
              user && user.role !== "end_user" && (user.role === "site_admin" || user.role === "admin" || user.role === "super_admin" || (user.role === "department_admin" && ["IT", "HR", "Administration"].includes(user.department))) ? (
                <HRRequestDetail 
                  user={user} 
                  logout={logout} 
                  previewRole={previewRole} 
                  setPreviewRole={setPreviewRole}
                  previewDealership={previewDealership}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/change-requests/:requestId"
            element={
              user ? (
                <ChangeRequestDetail user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <InstallPWA />
    </div>
    </RBACProvider>
    </ThemeProvider>
  );
}

export default App;